import {
  FormHelperText,
  Button,
  Typography,
  Grid,
  Box,
  Tooltip,
  LinearProgress,
  Chip,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import MainCard from "components/MainCard";
import { useDropzone } from "react-dropzone";
import serverCall from "api/serverCall";
import api from "api/api";
import { dispatch } from "store";
import { openSnackbar } from "store/reducers/snackbar";
import { useServerHealthContext } from "contexts/ServerHealthContext";
import { useEffect, useState } from "react";
import { MimicServerSocket } from "websocket/config";
import { Message } from "stompjs";
import { getOrderStatusColor } from "utils/common";

interface FormValues {
	file1: File | null;
	file2: File | null;
	file3: File | null;
}

interface WebsocketProgress {
	percentage: number;
	updateStatus: string;
	message: string;
}
type Props = {
	tabValue: number;
	fetchFno: () => void;
	fetchEquities: () => void;
};

const FileUploadCard: React.FC<Props> = ({
	tabValue,
	fetchEquities,
	fetchFno,
}) => {
	const { kiteStatus } = useServerHealthContext();
	const [progress, setProgress] = useState<WebsocketProgress | null>(null);

	const formik = useFormik<FormValues>({
		initialValues: {
			file1: null,
			file2: null,
			file3: null,
		},
		validationSchema: Yup.object().shape({
			file1: Yup.mixed().required("File 1 is required"),
			...(tabValue === 0 && {
				file2: Yup.mixed().required("File 2 is required"),
				file3: Yup.mixed().required("File 3 is required"),
			}),
			...(tabValue === 1 && {
				file2: Yup.mixed().required("File 2 is required"),
			}),
		}),

		onSubmit: async (values, { setSubmitting, resetForm }) => {
			try {
				if (!kiteStatus) {
					dispatch(
						openSnackbar({
							open: true,
							close: true,
							message: `Kite status: Not Connected`,
							variant: "alert",
							alert: {
								color: "error",
							},
							anchorOrigin: {
								vertical: "top",
								horizontal: "error",
							},
						})
					);
					return;
				}
				const formData = new FormData();
				if (values.file1)
					formData.append(
						tabValue == 0 ? "nseFile" : "upstoxFile",
						values.file1
					);
				if (values.file2)
					formData.append(
						tabValue == 0 ? "upstoxFile" : "iciciFile",
						values.file2
					);
				if (values.file3) formData.append("iciciFile", values.file3);

				try {
					if (tabValue == 0) {
						await serverCall.post<any>(api.uploadEquityInstruments(), formData);
					} else {
						await serverCall.post<any>(api.uploadFnoInstruments(), formData);
					}
				} catch (error) {
					console.error(error);
				} finally {
					// setEquitiesLoading(false);
				}
			} catch (error) {
				console.error("Upload failed:", error);
			} finally {
				setSubmitting(false);
			}
		},
	});

	const {
		values,
		errors,
		touched,
		handleSubmit,
		setFieldValue,
		setFieldTouched,
		setFieldError,
		resetForm,
		validateField,
	} = formik;

	const handleUploadStatus = (message: Message) => {
		const parsedValue = JSON.parse(message.body) as WebsocketProgress;
		setProgress(parsedValue);
		if (parsedValue?.percentage === 100) {
			resetForm();
			dispatch(
				openSnackbar({
					open: true,
					close: true,
					message: `File upload completed`,
					variant: "alert",
					alert: {
						color: "success",
					},
					anchorOrigin: {
						vertical: "top",
						horizontal: "error",
					},
				})
			);
			console.log(tabValue, "TAB VALUE>>>");

			if (tabValue === 0) {
				fetchEquities();
			} else {
				fetchFno();
			}
		}
		if (parsedValue.updateStatus === "FAILED") {
			setTimeout(() => {
				setProgress(null);
				resetForm();
			}, 10000);
		}
	};

	useEffect(() => {
		MimicServerSocket.subscribe(
			"/topic/instruments/progress",
			handleUploadStatus
		);

		return () => {
			MimicServerSocket.unsubscribe("/topic/instruments/progress");
		};
	}, []);

	const getLabel = (value: string) => {
		switch (value) {
			case "COMPLETED":
				return "Completed";
			case "IN_PROGRESS":
				return "In Progress";
			case "FAILED":
				return "Failed";
			default:
				break;
		}
	};

	const formatFileSize = (size: number) => {
		if (!size || size === 0) return "0 Bytes";
		const kb = size / 1024;
		if (kb < 1024) return `${kb.toFixed(1)} KB`;
		return `${(kb / 1024).toFixed(2)} MB`;
	};

	const DropzoneField = ({
		name,
		label,
	}: {
		name: keyof FormValues;
		label: string;
	}) => {
		const { getRootProps, getInputProps, isDragActive } = useDropzone({
			multiple: false,
			accept: {
				"text/csv": [],
				"application/vnd.ms-excel": [],
				"application/json": [],
				"text/plain": [],
			},
			onDrop: (acceptedFiles) => {
				if (!acceptedFiles.length) return;
				const file = acceptedFiles[0];
				setFieldValue(name, file, true);
				setFieldTouched(name, true, false);
				validateField(name);
			},
			onDropRejected: (fileRejections) => {
				if (!fileRejections.length) return;
				const reason =
					fileRejections[0].errors[0]?.message ?? "Invalid file type";
				setFieldError(name, `Unsupported file format. ${reason}`);
				setFieldTouched(name, true, false);
				setTimeout(() => {
					setFieldError(name, "");
					setFieldTouched(name, false, false);
				}, 10_000);
			},
		});

		return (
			<Grid item xs={12} md={4}>
				<Box
					{...getRootProps()}
					sx={{
						border: "2px dashed",
						borderColor: isDragActive ? "primary.main" : "#ccc",
						borderRadius: 2,
						p: 2,
						minHeight: 120,
						textAlign: "center",
						cursor: "pointer",
						backgroundColor: isDragActive ? "#f1f9ff" : "#fafafa",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
					}}>
					{/* This is the ONLY input – provided by react‑dropzone */}
					<input {...getInputProps()} name={name} />

					<Typography variant="body2" color="textSecondary">
						{values[name] ? (
							<>
								<strong>{values[name]?.name}</strong>
								<br />
								<Typography variant="caption" color="GrayText">
									{formatFileSize(values[name]?.size ?? 0)}
								</Typography>
							</>
						) : (
							<>
								Drag & drop or click to select <strong>{label}</strong>
							</>
						)}
					</Typography>

					{values[name] && (
						<Button
							size="small"
							color="error"
							onClick={(e) => {
								e.stopPropagation();
								setFieldValue(name, null);
								setFieldTouched(name, false);
								setFieldError(name, "");
							}}
							sx={{ mt: 1 }}>
							Remove
						</Button>
					)}

					{touched[name] && errors[name] && (
						<FormHelperText error sx={{ mt: 1 }}>
							{errors[name]}
						</FormHelperText>
					)}
				</Box>
			</Grid>
		);
	};

	return (
		<MainCard sx={{ overflow: "visible", position: "relative", p: 2 }}>
			<FormikProvider value={formik}>
				<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
					<Grid container spacing={3}>
						{tabValue === 0 && (
							<>
								<DropzoneField name="file1" label="NSE File" />
								<DropzoneField name="file2" label="Upstox File" />
								<DropzoneField name="file3" label="ICICI File" />
							</>
						)}

						{tabValue === 1 && (
							<>
								<DropzoneField name="file1" label="Upstox File" />
								<DropzoneField name="file2" label="ICICI File" />
							</>
						)}

						<Grid item xs={12} md={12}>
							<Grid item xs={12} md={12}>
								<Tooltip
									title={
										(tabValue === 0 &&
											(!values.file1 || !values.file2 || !values.file3)) ||
										(tabValue === 1 && (!values.file1 || !values.file2))
											? "Please select all required files before uploading"
											: ""
									}
									arrow
									placement="top">
									<Button
										type="submit"
										variant="contained"
										color="primary"
										sx={{
											mt: 2,
											opacity:
												(tabValue === 0 &&
													(!values.file1 || !values.file2 || !values.file3)) ||
												(tabValue === 1 && (!values.file1 || !values.file2))
													? 0.6
													: 1,
											pointerEvents:
												(tabValue === 0 &&
													(!values.file1 || !values.file2 || !values.file3)) ||
												(tabValue === 1 && (!values.file1 || !values.file2))
													? "none"
													: "auto",
										}}>
										Upload All Files
									</Button>
								</Tooltip>
								{progress &&
									progress.percentage > 0 &&
									progress.percentage !== 100 && (
										<>
											<Box
												sx={{ display: "flex", alignItems: "center", mt: 3 }}>
												<Box sx={{ width: "100%", mr: 1 }}>
													<LinearProgress
														variant="determinate"
														value={progress.percentage}
													/>
												</Box>
												<Box sx={{ minWidth: 35 }}>
													<Typography
														variant="body2"
														sx={{ color: "text.secondary" }}>{`${Math.round(
														progress.percentage
													)}%`}</Typography>
												</Box>
											</Box>
											<Grid
												container
												justifyContent="space-between"
												sx={{ mt: 1 }}>
												<Grid>{progress?.message}</Grid>
												<Chip
													variant="light"
													label={getLabel(progress.updateStatus)}
													color={getOrderStatusColor(
														progress.updateStatus.toLowerCase()
													)}
												/>
											</Grid>
										</>
									)}
							</Grid>
						</Grid>
					</Grid>
				</Form>
			</FormikProvider>
		</MainCard>
	);
};

export default FileUploadCard;
